/// <amd-module name="Build/Settings/github-fork-protection/GithubForkProtectionSettingsView" />
import { GeneralSettingsData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Settings/Resources";
import { SettingsToggle } from "Build/SettingsToggle/SettingsToggle";
import { Checkbox } from "VSSUI/Checkbox";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import * as React from "react";

import { BestPracticeSettings, CommentTriggerOption, DisabledSettings, ForkProtectionOption } from "Build/Common/Library/PipelineTriggerSettings";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { IGeneralSettingsService } from "../general/GeneralSettingsService";

class PipelineTriggerSettingsHelper {
    static isForkProtectionEnabled(settings: GeneralSettingsData): boolean {
        return settings.forkProtectionEnabled.orgEnabled || settings.forkProtectionEnabled.enabled;
    }

    static getOrgLevelCommentRequirementOption(settings: GeneralSettingsData): CommentTriggerOption {
        return !settings.requireCommentsForNonTeamMembersOnly.orgEnabled && !settings.requireCommentsForNonTeamMemberAndNonContributors.orgEnabled
            ? CommentTriggerOption.All
            : settings.requireCommentsForNonTeamMembersOnly.orgEnabled
            ? CommentTriggerOption.NonTeamMembers
            : CommentTriggerOption.NonTeamMembersNonContributor;
    }

    static getOrgLevelBuildProtectionOption(settings: GeneralSettingsData): ForkProtectionOption {
        if (!settings.forkProtectionEnabled.orgEnabled) {
            return ForkProtectionOption.None;
        }

        if (!settings.buildsEnabledForForks.orgEnabled) {
            return ForkProtectionOption.Disabled;
        }

        if (
            settings.buildsEnabledForForks.orgEnabled &&
            settings.enforceJobAuthScopeForForks.orgEnabled &&
            settings.enforceNoAccessToSecretsFromForks.orgEnabled &&
            settings.isCommentRequiredForPullRequest.orgEnabled &&
            !settings.requireCommentsForNonTeamMembersOnly.orgEnabled &&
            !settings.requireCommentsForNonTeamMemberAndNonContributors.orgEnabled
        ) {
            return ForkProtectionOption.BestPractice;
        }

        return ForkProtectionOption.Custom;
    }

    static getCommentRequirementOption(settings: GeneralSettingsData): CommentTriggerOption {
        var orgLevelRequiresComments =
            settings.forkProtectionEnabled.orgEnabled &&
            settings.buildsEnabledForForks.orgEnabled &&
            settings.isCommentRequiredForPullRequest.orgEnabled;
        var orgLevelCommentRequirement = this.getOrgLevelCommentRequirementOption(settings);

        var projectLevelCommentRequirement =
            !settings.requireCommentsForNonTeamMembersOnly.enabled && !settings.requireCommentsForNonTeamMemberAndNonContributors.enabled
                ? CommentTriggerOption.All
                : settings.requireCommentsForNonTeamMembersOnly.enabled
                ? CommentTriggerOption.NonTeamMembers
                : CommentTriggerOption.NonTeamMembersNonContributor;

        const _getCommentTriggerOptionIndex = (option: CommentTriggerOption): number => {
            return Object.keys(CommentTriggerOption).indexOf(option);
        }

        if (orgLevelRequiresComments && _getCommentTriggerOptionIndex(orgLevelCommentRequirement) > _getCommentTriggerOptionIndex(projectLevelCommentRequirement)) {
            return orgLevelCommentRequirement;
        }

        return projectLevelCommentRequirement;
    }

    static getBuildProtectionOption(settings: GeneralSettingsData): ForkProtectionOption {
        if (this._areBuildsDisabled(settings)) {
            return ForkProtectionOption.Disabled;
        }

        if (this.areBuildsBestPractice(settings)) {
            return ForkProtectionOption.BestPractice;
        }

        return ForkProtectionOption.Custom;
    }

    private static areBuildsBestPractice(settings: GeneralSettingsData): boolean {
        return (
            this.getOrgLevelBuildProtectionOption(settings) === ForkProtectionOption.BestPractice &&
            settings.buildsEnabledForForks.enabled &&
            settings.enforceJobAuthScopeForForks.enabled &&
            settings.enforceNoAccessToSecretsFromForks.enabled &&
            settings.isCommentRequiredForPullRequest.enabled &&
            !settings.requireCommentsForNonTeamMembersOnly.enabled &&
            !settings.requireCommentsForNonTeamMemberAndNonContributors.enabled
        );
    }

    private static _areBuildsDisabled(settings: GeneralSettingsData): boolean {
        return (settings.forkProtectionEnabled.orgEnabled && !settings.buildsEnabledForForks.orgEnabled) || !settings.buildsEnabledForForks.enabled;
    }
}

interface IPipelineTriggerSettingsState {
    selectedProtectionOption: ForkProtectionOption | undefined;
}

export class GithubForkProtectionSettingsView extends VssComponent<IVssComponentProperties, IPipelineTriggerSettingsState> {
    private _service: IGeneralSettingsService;
    private _settings: IReadonlyObservableValue<GeneralSettingsData>;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IGeneralSettingsService>("IGeneralSettingsService");
        this._settings = this._service.data;
        this.state = {
            selectedProtectionOption: undefined
        };
    }

    private _renderForkProtectionToggle(settings: GeneralSettingsData): JSX.Element {
        var handleForkProtectionToggle = (forkProtectionEnabled: boolean) => {
            this._service.updatePipelineTriggerSettings({ forkProtectionEnabled });
            // reset the selectedProtectionOption
            this.setState({ selectedProtectionOption: ForkProtectionOption.Disabled });
        };

        return (
            <SettingsToggle
                checked={settings.forkProtectionEnabled.orgEnabled || settings.forkProtectionEnabled.enabled}
                disabled={settings.viewOnlyPermissions || settings.forkProtectionEnabled.orgEnabled}
                title={Resources.ForkProtectionEnabled}
                description={Resources.ForkProtectionEnabledTooltip}
                onChange={(_, forkProtectionEnabled) => handleForkProtectionToggle(forkProtectionEnabled)}
                isHighRisk
                highRiskTitle={Resources.ForkProtectionEnabledToggleHighRiskTitle}
                learnMoreLink="https://go.microsoft.com/fwlink/?linkid=862029"
                toggleTooltipProps={settings.forkProtectionEnabled.orgEnabled ? { text: Resources.SettingEnforcedOrgLevel } : undefined}
            />
        );
    }

    private _handleCommentRequirementRadioButtonSelection = (settings: GeneralSettingsData, selectedId: string) => {
        if (selectedId === PipelineTriggerSettingsHelper.getCommentRequirementOption(settings)) {
            return;
        }

        switch (selectedId) {
            case CommentTriggerOption.All: {
                this._service.updatePipelineTriggerSettings({
                    forkProtectionEnabled: true,
                    buildsEnabledForForks: true,
                    isCommentRequiredForPullRequest: true,                    
                    requireCommentsForNonTeamMembersOnly: false,
                    requireCommentsForNonTeamMemberAndNonContributors: false
                });
                break;
            }
            case CommentTriggerOption.NonTeamMembers: {
                this._service.updatePipelineTriggerSettings({
                    forkProtectionEnabled: true,
                    buildsEnabledForForks: true,
                    isCommentRequiredForPullRequest: true,
                    requireCommentsForNonTeamMembersOnly: true,
                    requireCommentsForNonTeamMemberAndNonContributors: false
                });
                break;
            }
            case CommentTriggerOption.NonTeamMembersNonContributor: {
                this._service.updatePipelineTriggerSettings({
                    forkProtectionEnabled: true,
                    buildsEnabledForForks: true,
                    isCommentRequiredForPullRequest: true,
                    requireCommentsForNonTeamMembersOnly: false,
                    requireCommentsForNonTeamMemberAndNonContributors: true
                });
                break;
            }
        }
    };

    private _renderCommentRequirementSettings(settings: GeneralSettingsData): JSX.Element {
        const orgLevelBuildsEnabled = settings.forkProtectionEnabled.orgEnabled && settings.buildsEnabledForForks.orgEnabled;
        const orgLevelIsCommentRequired = orgLevelBuildsEnabled && settings.isCommentRequiredForPullRequest.orgEnabled;
        const orgLevelCommentRequirementOption = PipelineTriggerSettingsHelper.getOrgLevelCommentRequirementOption(settings);

        return (
            <div className="flex row margin-left-16 padding-left-16 margin-top-4">
                <RadioButtonGroup
                    selectedButtonId={PipelineTriggerSettingsHelper.getCommentRequirementOption(settings)}
                    onSelect={selectedId => {
                        this._handleCommentRequirementRadioButtonSelection(settings, selectedId);
                    }}
                >
                    <RadioButton
                        id={CommentTriggerOption.All}
                        text={Resources.RequireCommentsForAllPullRequests}
                        disabled={
                            settings.viewOnlyPermissions ||
                            (orgLevelIsCommentRequired && orgLevelCommentRequirementOption === CommentTriggerOption.All)
                        }
                        tooltipProps={
                            orgLevelIsCommentRequired && orgLevelCommentRequirementOption === CommentTriggerOption.All
                                ? { text: Resources.SettingEnforcedOrgLevel }
                                : undefined
                        }
                    />
                    <RadioButton
                        id={CommentTriggerOption.NonTeamMembers}
                        text={Resources.RequireCommentsForNonTeamMembersOnly}
                        disabled={
                            settings.viewOnlyPermissions ||
                            (orgLevelIsCommentRequired && orgLevelCommentRequirementOption === CommentTriggerOption.All)
                        }
                        tooltipProps={
                            orgLevelIsCommentRequired && orgLevelCommentRequirementOption === CommentTriggerOption.All
                                ? { text: Resources.SettingEnforcedOrgLevel }
                                : undefined
                        }
                    />
                    <RadioButton
                        id={CommentTriggerOption.NonTeamMembersNonContributor}
                        text={Resources.RequireCommentsForNonTeamMemberAndNonContributors}
                        disabled={
                            settings.viewOnlyPermissions ||
                            (orgLevelIsCommentRequired && orgLevelCommentRequirementOption !== CommentTriggerOption.NonTeamMembersNonContributor)
                        }
                        tooltipProps={
                            orgLevelIsCommentRequired && orgLevelCommentRequirementOption !== CommentTriggerOption.NonTeamMembersNonContributor
                                ? { text: Resources.SettingEnforcedOrgLevel }
                                : undefined
                        }
                    />
                </RadioButtonGroup>
            </div>
        );
    }

    private _renderCustomForkProtectionLevelSettings(settings: GeneralSettingsData): JSX.Element {
        const orgLevelBuildsEnabled = settings.forkProtectionEnabled.orgEnabled && settings.buildsEnabledForForks.orgEnabled;
        const isCommentRequired = (orgLevelBuildsEnabled && settings.isCommentRequiredForPullRequest.orgEnabled) || settings.isCommentRequiredForPullRequest.enabled;
        return (
            <div>
                <Checkbox
                    onChange={(_, enforceNoAccessToSecretsFromForks) =>
                        this._service.updatePipelineTriggerSettings({ forkProtectionEnabled: true, buildsEnabledForForks: true, enforceNoAccessToSecretsFromForks })
                    }
                    label={Resources.EnforceNoAccessToSecretsFromForks}
                    checked={(orgLevelBuildsEnabled && settings.enforceNoAccessToSecretsFromForks.orgEnabled) || settings.enforceNoAccessToSecretsFromForks.enabled}
                    disabled={settings.viewOnlyPermissions || (orgLevelBuildsEnabled && settings.enforceNoAccessToSecretsFromForks.orgEnabled)}
                    tooltipProps={orgLevelBuildsEnabled && settings.enforceNoAccessToSecretsFromForks.orgEnabled ? { text: Resources.SettingEnforcedOrgLevel } : undefined}
                />
                <Checkbox
                    onChange={(_, enforceJobAuthScopeForForks) => this._service.updatePipelineTriggerSettings({ forkProtectionEnabled: true, buildsEnabledForForks: true, enforceJobAuthScopeForForks })}
                    label={Resources.EnforceJobAuthScopeForForks}
                    checked={(orgLevelBuildsEnabled && settings.enforceJobAuthScopeForForks.orgEnabled) || settings.enforceJobAuthScopeForForks.enabled}
                    disabled={settings.viewOnlyPermissions || (orgLevelBuildsEnabled && settings.enforceJobAuthScopeForForks.orgEnabled)}
                    tooltipProps={orgLevelBuildsEnabled && settings.enforceJobAuthScopeForForks.orgEnabled ? { text: Resources.SettingEnforcedOrgLevel } : undefined}
                />
                <Checkbox
                    onChange={(_, isCommentRequiredForPullRequest) =>
                        this._service.updatePipelineTriggerSettings({ forkProtectionEnabled: true, buildsEnabledForForks: true, isCommentRequiredForPullRequest })
                    }
                    label={Resources.IsCommentRequiredForPullRequest}
                    checked={(orgLevelBuildsEnabled && settings.isCommentRequiredForPullRequest.orgEnabled) || settings.isCommentRequiredForPullRequest.enabled}
                    disabled={settings.viewOnlyPermissions || (orgLevelBuildsEnabled && settings.isCommentRequiredForPullRequest.orgEnabled)}
                    tooltipProps={orgLevelBuildsEnabled && settings.isCommentRequiredForPullRequest.orgEnabled ? { text: Resources.SettingEnforcedOrgLevel } : undefined}
                />
                {(isCommentRequired) &&
                    this._renderCommentRequirementSettings(settings)}
            </div>
        );
    }

    private _handleForkProtectionLevelRadioButtonSelection = (selectedId: string) => {
        const { selectedProtectionOption } = this.state;

        if (selectedId === selectedProtectionOption) {
            return;
        }

        this.setState({ selectedProtectionOption: selectedId as ForkProtectionOption });

        switch (selectedId) {
            case ForkProtectionOption.Disabled: {
                this._service.updatePipelineTriggerSettings(DisabledSettings);
                break;
            }
            case ForkProtectionOption.BestPractice: {
                this._service.updatePipelineTriggerSettings(BestPracticeSettings);
                break;
            }
            case ForkProtectionOption.Custom: {
                this._service.updatePipelineTriggerSettings(BestPracticeSettings); // custom settings are by default best practice
                break;
            }
        }
    };

    private _renderForkProtectionLevelSettings(settings: GeneralSettingsData): JSX.Element {
        const { selectedProtectionOption } = this.state;
        // Set the initial state of the option
        if (selectedProtectionOption === undefined) {
            this.setState({ selectedProtectionOption: PipelineTriggerSettingsHelper.getBuildProtectionOption(settings) });
        }

        const orgLevelBuildProtectionOption = PipelineTriggerSettingsHelper.getOrgLevelBuildProtectionOption(settings);

        return (
            <div className="github-fork-protection-settings flex-row padding-horizontal-20">
                <div className="flex-column margin-right-16 padding-right-16 base-toggle-width"></div>
                <div className="flex-column flex-grow margin-left-16 padding-horizontal-16">
                    <RadioButtonGroup
                        selectedButtonId={selectedProtectionOption}
                        onSelect={selectedId => {
                            this._handleForkProtectionLevelRadioButtonSelection(selectedId);
                        }}
                    >
                        <RadioButton
                            id={ForkProtectionOption.Disabled}
                            text={Resources.DisableBuildsInPullRequestsFromForkedRepositories}
                            ariaDescribedBy="forkProtectionOption-disabled"
                            disabled={settings.viewOnlyPermissions || orgLevelBuildProtectionOption == ForkProtectionOption.Disabled}
                            tooltipProps={
                                orgLevelBuildProtectionOption === ForkProtectionOption.Disabled
                                    ? { text: Resources.SettingEnforcedOrgLevel }
                                    : undefined
                            }
                        />
                        <div className="body-s margin-left-16 margin-bottom-2 padding-left-16" aria-labelledby="forkProtectionOption-disabled">
                            <span id="forkProtectionOption-disabled" className="secondary-text">
                                {Resources.DisableBuildsInPullRequestsFromForkedRepositoriesTooltip}
                            </span>
                        </div>
                        <RadioButton
                            id={ForkProtectionOption.BestPractice}
                            text={Resources.SecurelyBuildPullRequestsFromForkedRepositories}
                            ariaDescribedBy="forkProtectionOption-secure"
                            disabled={settings.viewOnlyPermissions || orgLevelBuildProtectionOption === ForkProtectionOption.Disabled}
                            tooltipProps={
                                orgLevelBuildProtectionOption === ForkProtectionOption.Disabled
                                    ? { text: Resources.SettingEnforcedOrgLevel }
                                    : undefined
                            }
                        />
                        <div className="body-s margin-left-16 margin-bottom-4 padding-left-16" aria-labelledby="forkProtectionOption-secure">
                            <span id="forkProtectionOption-secure" className="secondary-text">
                                {Resources.SecurelyBuildPullRequestsFromForkedRepositoriesTooltip}
                            </span>
                        </div>
                        <RadioButton
                            id={ForkProtectionOption.Custom}
                            text={Resources.CustomizeRulesOfBuildingPullRequestsFromForkedRepositories}
                            disabled={
                                settings.viewOnlyPermissions ||
                                orgLevelBuildProtectionOption === ForkProtectionOption.BestPractice ||
                                orgLevelBuildProtectionOption === ForkProtectionOption.Disabled
                            }
                            tooltipProps={
                                orgLevelBuildProtectionOption === ForkProtectionOption.BestPractice ||
                                orgLevelBuildProtectionOption === ForkProtectionOption.Disabled
                                    ? { text: Resources.SettingEnforcedOrgLevel }
                                    : undefined
                            }
                        />
                        <div className="flex row margin-left-16 padding-left-16 margin-top-4 full-width">
                            {selectedProtectionOption == ForkProtectionOption.Custom && this._renderCustomForkProtectionLevelSettings(settings)}
                        </div>
                    </RadioButtonGroup>
                </div>
            </div>
        );
    }

    private _renderSettings(settings: GeneralSettingsData): JSX.Element {
        const settingsLength = settings ? Object.keys(settings).length : 0;
        if (settingsLength === 0) {
            return <Spinner className="loading-spinner" label={Resources.LoadingText} />;
        }

        return (
            <>
                {this._renderForkProtectionToggle(settings)}
                {PipelineTriggerSettingsHelper.isForkProtectionEnabled(settings) && this._renderForkProtectionLevelSettings(settings)}
            </>
        );
    }

    public render(): JSX.Element {
        return (
            <Observer settings={this._settings}>
                {(observerProps: { settings: GeneralSettingsData }) => {
                    return <div className="settings-view">{this._renderSettings(observerProps.settings)}</div>;
                }}
            </Observer>
        );
    }
}
